<template>
    <v-container class="px-5 py-5" v-if="this.$store.state.signedIn">
        <v-flex xs12>
            <v-card>
                <v-toolbar dark flat color="secondary">
                    <v-toolbar-title :style="$store.state.textFormatting.headers">
                        Analytics System (click the elements and the arrows for explanation of each stage)
                    </v-toolbar-title>
                </v-toolbar>
                <v-layout class="px-2 py-2" justify-center align-center>
                    <v-flex xs2 class="px-2 py-2">
                        <v-btn :color="card_colours['message']" class="text-xs-center" @click="changeCard('message')">
                            <h3> Message </h3>
                        </v-btn>      
                    </v-flex>
                    <v-flex shrink class="px-2 py-2 text-xs-center">
                        <v-btn small :color="card_colours['a1']" @click="changeCard('a1')" icon><v-icon> arrow_forward </v-icon></v-btn>
                    </v-flex>
                    <v-flex xs2 class="px-2 py-2">
                        <v-btn :color="card_colours['event']" class="text-xs-center" @click="changeCard('event')">
                            <h3> Event </h3>
                        </v-btn>
                    </v-flex>
                    <v-flex shrink class="px-2 py-2 text-xs-center">
                        <v-btn small :color="card_colours['a2']" @click="changeCard('a2')" icon><v-icon> arrow_forward </v-icon></v-btn>
                    </v-flex>
                    <v-flex xs2 class="px-2 py-2">
                        <v-btn :color="card_colours['unit']" class="text-xs-center" @click="changeCard('unit')">
                            <h3> Refined Event </h3>
                        </v-btn>
                    </v-flex>
                    <v-flex shrink class="px-2 py-2 text-xs-center">
                        <v-btn small :color="card_colours['a3']" @click="changeCard('a3')" icon><v-icon> arrow_forward </v-icon></v-btn>
                    </v-flex>
                    <v-flex xs2 class="px-2 py-2">
                        <v-btn :color="card_colours['scored']" class="text-xs-center" @click="changeCard('scored')">
                            <h3> Scored Event </h3>
                        </v-btn>
                    </v-flex>
                    <v-flex shrink class="px-2 py-2 text-xs-center">
                        <v-btn small :color="card_colours['a4']" @click="changeCard('a4')" icon><v-icon> arrow_forward </v-icon></v-btn>
                        <v-btn small :color="card_colours['a5']" @click="changeCard('a5')" icon><v-icon> arrow_forward </v-icon></v-btn>
                    </v-flex>
                    <v-flex xs2 class="px-2 py-2">
                        <v-btn :color="card_colours['episode']" class="text-xs-center" @click="changeCard('episode')">
                            <h3> Episode </h3>
                        </v-btn>
                        <br />
                        <v-btn :color="card_colours['alert']" class="text-xs-center" @click="changeCard('alert')">
                            <h3> Alert? </h3>
                        </v-btn>
                    </v-flex>
                </v-layout>
            </v-card>
            
            <br />
        </v-flex>
        <v-flex xs12 class="py-2">
            <v-card v-if="selected">
                <v-toolbar dark flat color="secondary">
                    <v-toolbar-title>
                        {{ titles[selected] }}
                    </v-toolbar-title>
                    <v-spacer />
                    <v-flex shrink class="px-2 text-xs-center" v-if="mappings[selected]['b']">
                        <v-btn v-if="mappings[selected]['b']" color="success" icon small @click="changeCard(mappings[selected]['b'])"> <v-icon> arrow_left </v-icon></v-btn>
                    </v-flex>
                    <v-flex shrink class="px-2 text-xs-center" v-if="mappings[selected]['f']">
                        <v-btn v-if="mappings[selected]['f']" color="success" icon small @click="changeCard(mappings[selected]['f'])"><v-icon> arrow_right </v-icon></v-btn>
                    </v-flex>

                </v-toolbar>
                <v-card-text>
                    <div v-if="selected=='message'">
                        <p>
                            We begin with ingesting BGP messages from RouteViews, RIPE RIS Live and CSPs.
                        </p>
                        <p>
                            RouteViews accounts for approximately 20 million messages an hour, and sends messages batched in 15 minute intervals. 10 collectors total, spread globally.
                        </p>
                        <p>
                            RIPE RIS Live accounts for 20 million per hour, and messages are streamed in live from peers in near-real-time. 12 collectors total, spread globally.
                        </p>
                        <p>
                            CSP ingest accounts for 1 million messages per hour, with a mix of live streamed messages and batched messages. 4 CSPs total with one collector each.
                        </p>
                    </div>
                    <div v-if="selected=='a1'">
                        <p>
                            Messages are turned in to events via the event generation mechanism. We have one of these per collector (i.e. 10 for RouteViews, 12 for RIS Live and 1 per CSP), as the whole mechanism works on a peer by peer basis.
                        </p>
                        <p>
                            The system works by storing the current 'state' of the internet (which is pre-populated when the system is created), as seen from BGP messages. This works on a peer IP - prefix pair basis, so the view from one peer to a given prefix is treated exclusively to any other peer - prefix pairing.
                        </p>
                        <p>
                            When a message comes in, it is checked against this state, and any parent / child prefixes' state also (for enrichment, used later on). We call this parent / child check a <b>disaggregation</b> check.
                        </p>
                        <p>
                            Any changes are stored on the event, and the highest priority event from the list below is taken as the <b>prioritised change</b>:
                            <ol>
                                <li>
                                    <b>Observed direct assertion</b> <br />
                                    Change of ownership of the network prefix from one ASN to another (where the network prefix has been ‘observed’ i.e. a BGP message has been seen with the previous AS as the owner)
                                </li>
                                <li>
                                    <b>Observed Sub-ownership Assertion</b><br/>
                                    There exists an observed parent network prefix (less specific), seen specifically in BGP (not reference data), which the new owner of the given network prefix disagrees with.
                                </li>
                                <li>
                                    <b>Observed Path Change</b> <br/>
                                    The ownership ASN of this network prefix hasn’t changed but we have a new (and not seen before) path.
                                </li>
                                <li>
                                    <b>Observed Super-ownership Assertion</b><br />
                                    The exists child network prefix (more specific), seen specifically in BGP (not reference data), where new owner of the given network prefix disagrees with.
                                </li>
                                <li>
                                    <b>Reference Direct Assertion</b><br/>
                                    Where there exists an ownership ASN of the EVENT network prefix in registry data, but we haven’t seen this exact network prefix before (from this collector).
                                </li>
                                <li>
                                    <b>Reference Sub-ownership Assertion</b><br/>
                                    There exists a parent network prefix in registry data which has ownership ASN(s) which disagree with the new origin ASN of the EVENT network prefix.
                                </li>
                                <li>
                                    <b>Reference Super-ownership Assertion</b><br/>
                                    There exists a child network prefix in registry data which has ownership ASN(s) which disagree with the new origin ASN of the EVENT network prefix.
                                </li>
                                <li>
                                    <b>First Seen</b><br/>
                                    Where we haven’t seen this network prefix before. This will only ever happen if no parent/child network prefixes have been seen AND there is no reference data on parent/child network prefixes. ‘Seen before’ refers to individual peer ASNs i.e. if peer ASN 1 <b>has</b> seen a network prefix before but it is new for peer ASN 2, then ASN 2 will record this as a ‘First Seen’ EVENT.
                                </li>
                            </ol>
                        </p>
                        <p>
                            An example EVENT may have more than one <b>change</b> associated with it. If we have both an <b>observed path change</b> and one of the reference changes too, we will categorise the EVENT as, for example: <b>Path Change: Ongoing Sub-ownership Assertion</b>.
                            <br/>This is where the ownership ASN hasn’t changed, but it continues to disagree with the authorative owner of the parent network prefix. More on that later on.
                            <br/>Note that we can also end up <b>‘splitting’</b> a super-ownership assertion into multiple EVENTs (for each child network prefix found to be disagreeing). 
                            <br/>This is ‘capped’ in the following way: at most a netmask difference of 4, or the minimum (i.e. least specific) netmask found. An example is:
                            <ul>
                                <li>Event network prefix is a /20</li>
                                <li>/22’s, /24’s and /26’s found as children network prefixes</li>
                                <li>Only the /22’s and /24’s will be taken</li>
                                <li>In absence of the /22’s and /24’s, the /26’s would be used instead</li>
                            </ul>
                        </p>
                        <p>
                            <h2>Type Derivation</h2>
                        </p>
                        <p>
                            There are many compounded ‘types’ of EVENTs available, from various combinations of the above set of 8 EVENT types, and here we will explore how they are created. These appear on the website under ‘Event Type’ header in the EVENT inspection page.
                            <ul>
                                <li>
                                    <b>Path Change</b>
                                    <ul>
                                        <li>
                                            <b>Direct data agrees</b><br>
                                            There exists direct registry ASN with agreeing data (i.e. same ASN as the new origin).
                                        </li>
                                        <li>
                                            <b>Ongoing direct assertion</b><br/>
                                            The origin ASN continues to disagree with direct registry data
                                        </li>
                                        <li>
                                            <b>Ongoing subownership assertion</b><br/>
                                            The origin ASN continues to disagree with registry data of parent network prefix
                                        </li>
                                        <li>
                                            <b>Ongoing superownership assertion(s)</b><br/>
                                            As above but for child network prefix(es)
                                        </li>
                                    </ul>
                                    We also detect downstream ASNs here, e.g. if the new AS path is 1 2 3 4, and the network prefix appears in registry owned by AS 3, we have a <b>downstream</b> possibility. This is also detected on <b>parent</b> data in type 3.
                                    <br/>We also check in case 1. if the parent data happens to agree e.g. if the new path is 6 7 8 9 and the direct ASN is <b>not owned</b> by AS 9, but the parent network prefix happens to be, we flag this (for use later on in scoring).
                                </li>
                                <li>
                                    <b>Ownership Assertion</b>
                                    <ul>
                                        <li>
                                            <b>Parent data agrees</b><br/>
                                            There exists a parent network prefix with matching registry data to the new origin ASN.
                                        </li>
                                        <li>
                                            <b>Direct downstream</b><br/>
                                            Similar to the path changes, we detect downstream opportunities.
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <b>Sub-ownership Assertion</b><br/>
                                    (Same as ownership assertion other than we check direct data instead of parent)
                                </li>
                                <li>
                                    <b>First Seen</b>
                                    <ul>
                                        <li>
                                            <b>Direct data agrees</b><br/>
                                            The least worrying EVENT possible – a new network prefix is announced with matching registry data
                                        </li>
                                        <li>
                                            <b>Fragment (obs./ ref.)</b><br/>
                                            There exists a parent network prefix in the (observed / registry) with a matching ASN to the EVENT origin ASN.
                                        </li>
                                        <li>
                                            <b>Child data agrees</b><br/>
                                            There exists a child network prefix in the observed space with a matching ASN.
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <b>Super-ownership Assertion</b>
                                    <ul>
                                        <li>
                                            <b>Downstream</b><br/>
                                            For example: we have AS path of 4 5 6 7 and the <b>child</b> network prefix has ownership ASN of 6. Rare occurrence but have seen it happen.
                                        </li>
                                    </ul>
                                </li>
                                Note that we can have an EVENT subscribe to <b>no additional types</b>, e.g. a plain ‘ownership assertion’ with no downstream or other agreeing data.
                            </ul>
                        </p>
                    </div>
                    <div v-if="selected=='event'">
                        <p>
                            The system process messages down to around <b>6-10 million events per 24 hours</b>. Approximately 75% of EVENTs are path changes (down from 90% in the current system), 15% are ownership assertions, 
                            with the rest of the EVENTs split among first seens, sub-ownership assertions etc. Worth also noting that some EVENTs in the old system would be labelled as <b>‘sub-ownership assertion’</b> where 
                            in the new system they could be <b>‘Path Change: Ongoing Sub-ownership Assertion’</b>, so the 75% figure in fact is a lot lower if taking a direct comparison. 
                            <br/>Over time, the volume of ‘First Seen’ EVENTs should decrease as each peer sees more advertisements.
                            The high volume of path changes can be attributed to multiple paths to the same destination. Since the original trials further work has been introduced to ‘dampen’ this high flux in path changes, 
                            remembering the various routes and only creating a path change EVENT if the new path is deemed new suspected change, rather than just a repeat of a previous path advertisement.
                        </p>
                    </div>
                    <div v-if="selected=='a2'">
                        <p>
                            Once we have derived the additional EVENT type, we send the EVENT to the test element of the analytics system. There are many tests to apply in various circumstances, as follows:
                            <ol>
                                <li>
                                    <b>Controlled / Restricted Prefix Check</b><br/>
                                    We store globally ‘restricted DNS’ and the user defined ‘controlled (restricted) network prefixes’ in the same detection engine. This distinguishes between the two.
                                </li>
                                <li>
                                    <b>ASN in path risk</b><br/>
                                    Where we have new ASNs in the path, we check to see how prominent they are. An ASN with very low presence in AS paths is deemed more likely to be a malicious ASN. 
                                    Scores between <b>0 and 1 (0 for no risk i.e. all are very popular ASNs, 1 for most risk i.e. at least one very unpopular ASN)</b>. Calculated as follows:
                                    <ol>
                                        <li>Each ASN is scored for 0 to 1, 1 being never seen before (in 8 days), 0.8 for &lt;0.05 of average ASN popularity, 0.6 for &lt;0.1 avg. ASN pop., 0.2 for &lt;0.5, else it will score zero.</li>
                                        <li>From highest to lowest, scores are <b>compound added</b>, best explained by an example. If we have scores as follows: <b>[0, 0.8 , 0.4, 0.2]</b>, we begin at 0.8, then add (1 – 0.8) * 0.4 = 0.08, to get us to 0.88. We then add (1 – 0.88) * 0.2 = 0.024 to get us to <b>0.904</b>.</li>
                                    </ol>
                                </li>
                                <li>
                                    <b>Edit Difference</b><br/>
                                    Edit distance / max(length of old path, length of new path), where edit distance = #swaps/deletions/additions to get from one path to the other. <b>Between 0 and 1</b> with 1 being complete path difference, 0 being exactly the same path.
                                </li>
                                <li>
                                    <b>Path Change Type</b><br/>
                                    In the case of a path change, detect the path change type e.g. 
                                    <br/>(old) 1 2 3 4 -> (new) 1 6 7 4 is type 1, 
                                    <br/>(old) 1 2 3 4 -> (new) 1 8 3 4 is type 2.
                                </li>
                                <li>
                                    <b>Route Leak</b><br/>
                                    In case of an ownership change, detect route leak e.g. (old) 1 2 3 4 -> (new) 1 2 3 is a Route Leak. 
                                    <br/>A Route Leak is “the propagation of routing announcement(s) beyond their intended scope” -RFC 7908
                                </li>
                                <li>
                                    <b>Common Owner</b><br/>
                                    If the registry entry for two ASNs is the same in ‘name’ or ‘description’ we have a definite match; else we apply other pattern matches as follows, to return a ‘possible’ match:
                                    <ol>
                                        <li>Check words in description for possible matches, excluding common words and words less than 4 chars.</li>
                                        <li>Check name for >0.8 similarity (i.e. ‘word’ and ‘words’ would suffice)</li>
                                        <li>Check name denumerated (numbers removed) for >0.9 similarity e.g. TT-AS-01345 and TT-AS-03452 would suffice</li>
                                        <li>Check words in one description against the name of the other, >0.9 similarity</li>
                                        <li>Check the description of one ASN for abbreviation of the other AS’s name (i.e. High Winds Network Group vs. HWNG) for >0.7 similarity</li>
                                    </ol>
                                    We apply the common owner in different ways depending on the EVENT type:
                                    <ul>
                                        <li>
                                            <b>Ownership Assertion</b><br/>
                                            Check for common owner between new and old origin ASNs
                                        </li>
                                        <li>
                                            <b>Path Change: Ongoing...</b>
                                            <ul>
                                                <li>
                                                    <b>...Ownership Assertion</b><br/>
                                                    Check vs. each ASN in registry for the EVENT network prefix, store the best result (definite, followed by possible, followed by none)
                                                </li>
                                                <li>
                                                    <b>...Sub-ownership Assertion</b><br/>
                                                    Check vs. each ASN in registry for parent network prefix, store the best result (definite, followed by possible, followed by none)
                                                </li>
                                                <li>
                                                    <b>...Super-ownership Assertion</b><br/>
                                                    Omitted for efficiency and necessity
                                                </li>
                                            </ul>
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <b>Ownership Geo</b><br/>
                                    Check the geographic distribution (see 'ASN Baseline' for deatils) of the new and old origin ASNs. Also applies similar rules as the common owner in the case of a path change. Works as follows:
                                    <ol>
                                        <li>
                                            Compare the ‘max’ geolocations of both ASNs e.g. if AS 1 is 80% UK and AS 2 is 60% UK we have a match. 
                                            If an ASN is split 50/50 we take both as the max. If we have a match here set the initial score to 0.7. 
                                            Else, check to see if the max of each one is present in the other breakdown e.g. if AS 1 is 80% UK and AS 2 is 5% UK (or even 0.01%), we have a match. 
                                            In this case, set initial score to 0.4. Else, initial score is zero.
                                        </li>
                                        <li>
                                            Now, take the cross-correlation of the two i.e. for each geolocation key, if it is present in the other breakdown, take the minimum of the two breakdown scores. Summing this will give a score between 0 and 1.
                                        </li>
                                        <li>
                                            Now take <b>initial score + 0.3 * cross-correlation</b> as the final score.
                                        </li>
                                    </ol>
                                    Example below:
                                    <ol>
                                        <li>
                                            AS 1 is { UK : 0.5, FR: 0.25, GE: 0.25 }
                                            <br/>AS 2 is { PR: 0.8, UK: 0.1, FR: 0.1 }
                                        </li>
                                        <li>
                                            Initial score is 0.4 (from UK)
                                        </li>
                                        <li>
                                            Cross correlation is 0.2 (0.1 for UK, 0.1 for FR)
                                        </li>
                                        <li>
                                            So final correlation is 0.4 + 0.3 * 0.2 = 0.46
                                        </li>
                                    </ol>
                                </li>
                                <li>
                                    <b>Prefix vs. ASN geo</b><br/>
                                    Similar methodology to the ownership geo, except we compare the new origin ASN to the EVENT network prefix. 
                                    Exactly the same calculation so I won’t do it again, however it’s worth explaining how we get the network prefix geographic breakdown:
                                    <ul>
                                        <li>
                                            Let’s say we have a /24 network prefix, with a /20 parent in the UK, and two /26 children, one in FR and one in GE. 
                                            If we were to have a <b>geolocation for the exact network prefix, we would take that and stop here</b>, however in our case we do not, and we prefer the more specific network prefix geolocation. 
                                            Each /26 accounts for ¼ of the overall IP space of the /24, so we begin with ¼ FR and ¼ GE. The remaining half is filled with the parent geolocation, of the UK: { UK: 0.5, FR: 0.25, GE: 0.25 }
                                        </li>
                                        <li>
                                            If we were to also have let’s say a /27 which sits under the FR /26, lets say also in UK, we would take half of the weight of the /26 to now be the UK: { UK: 0.625, FR: 0.125, GE: 0.25 }
                                        </li>
                                    </ul>
                                </li>
                            </ol>
                        </p>
                    </div>
                    <div v-if="selected=='unit'">
                        <p>
                            Once we’ve run all the appropriate tests, we are on to the scoring, of which there are two parts: the scoring matrix and the scoring application.
                        </p>
                    </div>
                    <div v-if="selected=='a3'">
                        <p>
                            <h2>Scoring Matrix</h2><br/>
                            Different EVENT types are deemed to be more likely to be a BGP hijack, hence have the scoring applied in different ways. 
                            Similarly, having an ongoing ownership assertion is less impactful than a new ownership assertion. 
                            We have the following matrix to apply the scoring, which will then be used later on in the scoring application
                        </p>
                        <p>
                            <v-data-table
                                :headers="matrix_headers"
                                :items="scoring_matrix"
                                :hide-default-footer="true"
                                :pagination.sync="pagination"
                                :rows-per-page-items="rowsPerPageItems"
                            >
                                <template slot="headerCell" slot-scope="props">
                                    <v-tooltip top eager>
                                    <span
                                        slot="activator"
                                        :style="$store.state.textFormatting.main"
                                    >{{ props.header.text }}</span>
                                    <span :style="$store.state.textFormatting.main">{{ props.header.ad_text }}</span>
                                    </v-tooltip>
                                </template>
                                <template slot="items" slot-scope="props">
                                    <tr>
                                    <td
                                        class="text-xs-center"
                                        :style="$store.state.textFormatting.main"
                                    >{{ props.item.name }}</td>
                                    <td
                                        class="text-xs-center"
                                        :style="$store.state.textFormatting.main"
                                    >{{ props.item.init }}</td>
                                    <td
                                        class="text-xs-center"
                                        :style="$store.state.textFormatting.main"
                                    >{{ props.item.exp }}</td>
                                    </tr>
                                </template>
                            </v-data-table>
                        </p>
                        <p>
                            <b>[1]</b> Of the BGP messages this ASN has been present in, what proportion of those are with this ASN as the origin 
                            or if this ASN was in the [origin – 1] position (i.e. one away from origin towards the peer), with an origin ASN of either: 1) common owner or 2) DDoS mitigator. 
                            Score between 0 and 1. (Derived from BGP message data prior to EVENT analysis)
                        </p>
                        <p>
                            <b>[2]</b> Compare the amount of advertisements from this ASN to the average amount of all ASNs. 
                            If this ASN has never advertised before, flag as ‘no_adv’, else check the past 3 days, and if all of following are satisfied: 
                            last day &lt;0.05 average, 2 days back &lt;0.1 average, 3 days back &lt;0.2 average, then flag as ‘low_adv’.
                        </p>
                        <p>
                            <b>[3]</b> From the BGP message data, we can see whether this ASN has been the only, or accounts for most of the advertisements for this network prefix over the past 8 days. 
                            If it’s the only ASN, we flag this, else we take the 3 day origin proportion for this network prefix/ ASN pair. We use this to dampen scoring later on.
                        </p>
                        <p>
                            Now each EVENT type is treated differently, and the scoring matrix is changed in the following way (H = Hijack Score, I = Impact Score):
                        </p>
                        <p>

                            <v-data-table
                                :headers="adj_matrix_headers"
                                :items="adj_scoring_matrix"
                                :hide-default-footer="true"
                                :pagination.sync="pagination2"
                                :rows-per-page-items="rowsPerPageItems2"
                                v-if="adj_buffer"
                            >
                                <template slot="headerCell" slot-scope="props">
                                    <v-tooltip top eager>
                                    <span
                                        slot="activator"
                                        :style="$store.state.textFormatting.main"
                                    >{{ props.header.text }}</span>
                                    <span :style="$store.state.textFormatting.main">{{ props.header.ad_text }}</span>
                                    </v-tooltip>
                                </template>
                                <template slot="items" slot-scope="props">
                                    <tr>
                                        <td
                                            class="text-xs-center"
                                            :style="$store.state.textFormatting.main"
                                        >{{ props.item.name }}</td>
                                        <td
                                            class="text-xs-center"
                                            :style="$store.state.textFormatting.main"
                                        >{{ props.item.subtype }}</td>
                                        <td
                                            class="text-xs-center"
                                            :style="$store.state.textFormatting.main"
                                        >{{ props.item.base_scores }}</td>
                                        <td
                                            class="text-xs-center"
                                            :style="$store.state.textFormatting.main"
                                            v-if="adj_buffer"
                                        >
                                            <v-layout align-center justify-center>
                                                <v-flex shrink  align-center justify-center class="text-xs-center">
                                                    <v-btn @click="props.item.show_m=!props.item.show_m" v-if="props.item.show_m" dark fab small color="blue">
                                                        <h2>-</h2>
                                                    </v-btn>
                                                    <v-btn @click="props.item.show_m=!props.item.show_m" v-if="!props.item.show_m" dark fab small color="blue">
                                                        <h2>+</h2>
                                                    </v-btn>
                                                </v-flex>
                                                <v-flex v-if="props.item.show_m">
                                                    <v-card>
                                                        <ul id="parent">
                                                            <li v-for="key in props.item.matrix" :key="key.index">
                                                                {{ key.name }}: {{ key.value }}
                                                            </li>
                                                        </ul>
                                                    </v-card>
                                                </v-flex>
                                            </v-layout>
                                        </td>
                                    </tr>
                                </template>
                            </v-data-table>
                        </p>
                        <p>
                            <b>CIDR</b>: Netmask of the network prefix (i.e. 24 if network prefix = 1.1.1.1/24)
                            <br/>
                            <b>Scope</b>: Difference in netmask of EVENT network prefix and parent/child network prefix e.g. if the EVENT network prefix is /24 and parent is /18, scope is 6
                        </p>
                        <p>
                            We use the coefficients generated by the scoring matrix to multiply by the results from the testing section to get out final result.
                        </p>
                        <p>
                            The scores are applied as follows (<b>all scores are multiplied by the matrix coefficient</b>, and <b>C</b> represents the actual score of the test where applicable e.g. for path change type scoring, <b>C == path change type</b>):
                        </p>
                        <p>
                            <v-data-table
                                :headers="scoring_headers"
                                :items="scoring_items"
                                :hide-default-footer="true"
                                :pagination.sync="pagination"
                                :rows-per-page-items="rowsPerPageItems"
                            >
                                <template slot="headerCell" slot-scope="props">
                                    <v-tooltip top eager>
                                    <span
                                        slot="activator"
                                        :style="$store.state.textFormatting.main"
                                    >{{ props.header.text }}</span>
                                    <span :style="$store.state.textFormatting.main">{{ props.header.ad_text }}</span>
                                    </v-tooltip>
                                </template>
                                <template slot="items" slot-scope="props">
                                    <tr>
                                    <td
                                        class="text-xs-center"
                                        :style="$store.state.textFormatting.main"
                                    >{{ props.item.name }}</td>
                                    <td
                                        class="text-xs-center"
                                        :style="$store.state.textFormatting.main"
                                    >{{ props.item.h }}</td>
                                    <td
                                        class="text-xs-center"
                                        :style="$store.state.textFormatting.main"
                                    >{{ props.item.i }}</td>
                                    </tr>
                                </template>
                            </v-data-table>
                        </p>
                        <p>
                            We also have two edge cases:
                            <ol>
                                <li><b>Restricted DNS</b>: Where the advertised network network prefix encompasses known root DNS servers; both the Hijack and Impact scores are set to 99. </li>
                                <li><b>BOGON</b>: Where the advertisement relates to non-routable IP address space; both the Hijack and Impact scores are set to 0. </li>
                            </ol>
                        </p>
                        <p>
                            The scorings presented here are a working implementation to attempt to categorise suspected maliciousness of BGP activity. 
                            To further help the user asses the relative scores, there is then a normalisation process which alters the scoring using a simple normalisation formula.
                            <br/>This ensures all scores lie between 0 and 99 with as close to a linear distribution as possible (without skewing scores too far). 
                            The scoring is out of 100. But we enforce that the maximum a Hijack score can be is 99. 
                            This is intentional as we wish to demonstrate that this is an attempt to assess the maliciousness of an EVENT, but using these analytics alone and without further context we can never be 100% certain.
                            <br />The formula for normalisation is as follows, with Y = final score and X = pre-normalisation score:
                            
                            <v-flex class="text-xs-center" xs12>
                                <br />
                                Y = 0.569 * X + 2.845
                            </v-flex>
                            <br/>
                            This is based on the minimum possible score being -5 and the highest possible being 167 (pre-normalisation).
                        </p>

                    </div>
                    <div v-if="selected=='scored'">
                        <p>
                            We now have scored events, which are stored ready for access by the website. Note that events created by the dummy injection process are stored in a separate table, but are treated exactly the same in the system.
                        </p>
                    </div>
                    <div v-if="selected=='a4'">
                        <p>
                            We group events in to episode by the following key:
                            <ul>
                                <li>Prefix</li>
                                <li>Origin ASN Old</li>
                                <li>Origin ASN New</li>
                                <li>5 minute time window</li>
                            </ul>
                            Though this can miss events occurring on the boundary or the 5 minute window, or involving the said network prefix / ASNs, we have inspection tools on the alert inspection page to view these.
                        </p>
                    </div>
                    <div v-if="selected=='episode'">
                        <p>
                            Episodes are groups of events, see previous step for grouping method.
                        </p>
                    </div>
                    <div v-if="selected=='a5'">
                        <p>
                            The alerting mechanism works on ASNs and restricted prefixes that the user inputs, to either their organisation or individual user settings. The mechanism for alerting is given on the 'Edit ASNs' and 'Edit Restricted Prefixes' pages.
                        </p>
                    </div>
                    <div v-if="selected=='alert'">
                        <p>
                            Alerts are organisation / user specific and cannot be seen by any other organisation (or user within your organisation if user specific). These are visible throughout the website.
                        </p>
                    </div>
                </v-card-text>
            </v-card>
        </v-flex>
    </v-container>
</template>

<script>

import info from '../../shared/operational/infopopout.vue';


export default {
  name: 'analytics',
  icons: {
      iconfont: 'mdi-js'
  },
  components: {

  },
  data () {
    return {
        card_colours: null,
        light_colours: {
            message: '#ffffff',
            event: '#ffffff',
            unit: '#ffffff',
            scored: '#ffffff',
            episode: '#ffffff',
            alert: '#ffffff',
            a1: '#ffffff',
            a2: '#ffffff',
            a3: '#ffffff',
            a4: '#ffffff',
            a5: '#ffffff'
        },
        dark_colours: {
            message: '#737373',
            event: '#737373',
            unit: '#737373',
            scored: '#737373',
            episode: '#737373',
            alert: '#737373',
            a1: '#424242',
            a2: '#424242',
            a3: '#424242',
            a4: '#424242',
            a5: '#424242'
        },
        selected_colour: '#00e0f0',
        dark_select: '#006666',
        light_select: '#00e0f0',
        selected: null,
        titles: {
            message: 'Ingest Message',
            event: 'Event',
            unit: 'Refined Event',
            scored: 'Scored Event',
            episode: 'Episode (grouped Events)',
            alert: 'Alert (user specific)',
            a1: 'Event detection and categorisation',
            a2: 'Event refinement',
            a3: 'Scoring the event',
            a4: 'Aggregating into episodes',
            a5: 'Generation of alerts'
        },
        mappings: {
            message: {
                b: false,
                f: 'a1'
            },
            a1: {
                b: 'message',
                f: 'event'
            },
            event: {
                b: 'a1',
                f: 'a2'
            },
            a2: {
                b: 'event',
                f: 'unit'
            },
            unit: {
                b: 'a2',
                f: 'a3'
            },
            a3: {
                b: 'unit',
                f: 'scored'
            },
            scored: {
                b: 'a3',
                f: 'a4'
            },
            a4: {
                b: 'scored',
                f: 'episode'
            },
            episode: {
                b: 'a4',
                f: false
            },
            a5: {
                b: 'scored',
                f: 'alert'
            },
            alert: {
                b: 'a5',
                f: false
            }
        },
        dm: this.$store.state.darkMode,
        matrix_headers: [        
            {
                text: "Name",
                value: "name",
                align: "centre",
                ad_text: "Name of the matrix element (condensed name)",
                sortable: false
            },
            {
                text: "Initial value",
                value: "init",
                align: "centre",
                ad_text: "Initial value, changed by the various event metadata",
                sortable: false
            },
            {
                text: "Explanation",
                value: "exp",
                align: "centre",
                ad_text: "What this element actually means",
                sortable: false
            },
        ],
        scoring_headers: [
            {
                text: "Name",
                value: "name",
                align: "centre",
                ad_text: "Name of the scoring element",
                sortable: false
            },
            {
                text: "Hijack Score",
                value: "h",
                align: "centre",
                ad_text: "What difference this makes to the hijack score",
                sortable: false
            },
            {
                text: "Impact Score",
                value: "i",
                align: "centre",
                ad_text: "What difference this makes to the impact score",
                sortable: false
            },
        ],
        scoring_items: [
            {
                name: 'Base',
                h: 'H (base)',
                i: 'I (base)'
            },
            {
                name: 'Path Change Type',
                h: '10 * (2 – C) [min zero]',
                i: '-'
            },
            {
                name: 'Edit Difference',
                h: '10 * C',
                i: '-'
            },
            {
                name: 'Prefix vs ASN geo',
                h: '30 * (1 - C)',
                i: '50 * (1 - C)'
            },
            {
                name: 'Ownership geo',
                h: '30 * (1 - C)',
                i: '-'
            },
            {
                name: 'Proportion origin',
                h: '20 (if <0.1), 5 (if <0.5)',
                i: '-'
            },
            {
                name: 'Low advertisement',
                h: '20 (if ‘no_adv’), 5 (if ‘low_adv’)',
                i: '-'
            },
            {
                name: 'Proportion peers exposed',
                h: '-',
                i: '10 (if >0.9), 5 (if >0.5)'
            },
            {
                name: 'AS in path risk',
                h: '20 * C',
                i: '-'
            },
            {
                name: 'ASN origination (C == 3 day origin proportion)',
                h: '-30 (if ‘always’) , -20 * (C – 0.5) (if C>0.5)',
                i: '-'
            },
            {
                name: 'DDoS Mitigator',
                h: '-5',
                i: '-'
            },
            {
                name: 'Superownership / Routeleak',
                h: 'Divide by 2, cap at 40',
                i: '-'
            },
        ],
        scoring_matrix: [
            {
                name: 'Base (base)',
                init: 1,
                exp: 'Base score for the EVENT type'
            },
            {
                name: 'Path Change type (pc_type)',
                init: 0,
                exp: 'Scoring for the type of path change'
            },
            {
                name: 'Edit Difference (ed_diff)',
                init: 1,
                exp: 'Scoring from edit difference test'
            },
            {
                name: 'Prefix vs. ASN geo (pre_v_asn)',
                init: 1,
                exp: 'Scoring for the prefix vs. ASN geo test'
            },
            {
                name: 'Ownership Geo (own_geo)',
                init: 1,
                exp: 'Scoring for the ownership geo test'
            },
            {
                name: 'Proportion Origin (prop_orig)',
                init: 1,
                exp: 'see below [1]'
            },
            {
                name: 'Low advertisement (low_adv)',
                init: 1,
                exp: 'see below [2]'
            },
            {
                name: 'Prop. Peers exposed (prop_seen)',
                init: 1,
                exp: '# peers, of the total peers available that have seen this ASN advertising the EVENT network prefix'
            },
            {
                name: 'AS in path risk (as_in_path)',
                init: 1,
                exp: 'Scoring for the ASN in path risk test'
            },
            {
                name: 'Is super/ routeleak (is_super)',
                init: 0,
                exp: 'If we have a superownership assertion or a Route Leak'
            },
            {
                name: 'ASN origination (asn_orig)',
                init: 0,
                exp: 'see below [3]'
            }
        ],
        adj_matrix_headers: [
            {
                text: "Event Type",
                value: "name",
                align: "centre",
                ad_text: "The main event type",
                sortable: false
            },
            {
                text: "Event Subtype",
                value: "time",
                align: "centre",
                ad_text: "The additional information from the event",
                sortable: false
            },
            {
                text: "Base scores",
                value: "base_scores",
                align: "centre",
                ad_text: "Scores the system gives for the event type, before any manipulation",
                sortable: false
            },
            {
                text: "Scoring Matrix",
                value: "matrix",
                align: "centre",
                ad_text: "The scoring matrix for this exact combination",
                sortable: false
            },
        ],
        adj_scoring_matrix: [
            {
                name: 'First Seen',
                show_m: false,
                base_scores: 'H: 0, I: 0',
                subtype: '<any> agrees / Fragment',
                matrix: {
                    Base: 0,
                    Pc_type: 0,
                    Ed_diff: 0,
                    Pre_v_asn: 0,
                    Own_geo: 0,
                    Prop_orig: 0,
                    Low_adv: 0,
                    Prop_seen: 0,
                    As_in_path: 0,
                    Is_super: 0,
                    Asn_orig: 0,
                }
            },
            {
                name: 'First Seen',
                show_m: false,
                base_scores: 'H: 0, I: 0',
                subtype: 'No additional data',
                matrix: {
                    Base: 0,
                    Pc_type: 0,
                    Ed_diff: 0,
                    Pre_v_asn: 0.5,
                    Own_geo: 0,
                    Prop_orig: 0.5,
                    Low_adv: 1,
                    Prop_seen: 1,
                    As_in_path: 0,
                    Is_super: 0,
                    Asn_orig: 0,
                }
            },
            {
                name: 'Super-ownership Assertion',
                show_m: false,
                base_scores: 'H: 0, I: 15',
                subtype: 'Definite common owner',
                matrix: {
                    Base: 0,
                    Pc_type: 0,
                    Ed_diff: 0,
                    Pre_v_asn: 0,
                    Own_geo: 0,
                    Prop_orig: 0,
                    Low_adv: 0,
                    Prop_seen: 0,
                    As_in_path: 0,
                    Is_super: 0,
                    Asn_orig: 0,
                }
            },
            {
                name: 'Super-ownership Assertion',
                show_m: false,
                base_scores: 'H: 0, I: 15',
                subtype: 'Potential (downstream / common owner)',
                matrix: {
                    Base: 0.5,
                    Pc_type: 0,
                    Ed_diff: 1,
                    Pre_v_asn: 0.5,
                    Own_geo: 0.5,
                    Prop_orig: 0.5,
                    Low_adv: 1,
                    Prop_seen: 1,
                    As_in_path: 1,
                    Is_super: 1,
                    Asn_orig: 1
                }
            },
            {
                name: 'Super-ownership Assertion',
                show_m: false,
                base_scores: 'H: 0, I: 15',
                subtype: 'No additional data',
                matrix: {
                    Base: 1,
                    Pc_type: 0,
                    Ed_diff: 1,
                    Pre_v_asn: 1,
                    Own_geo: 1,
                    Prop_orig: 1,
                    Low_adv: 1,
                    Prop_seen: 1,
                    As_in_path: 1,
                    Is_super: 1,
                    Asn_orig: 1
                }
            },
            {
                name: 'Sub-ownership Assertion',
                show_m: false,
                base_scores: 'H: 50 [30 if ref.] + 10 / (2 ^ ([32 or 128] – CIDR)), I: 15 / (2 ^ (scope))',
                subtype: 'Direct/parent data agrees',
                matrix: {
                    Base: 0.1,
                    Pc_type: 0,
                    Ed_diff: 1,
                    Pre_v_asn: 0,
                    Own_geo: 0,
                    Prop_orig: 0,
                    Low_adv: 1,
                    Prop_seen: 1,
                    As_in_path: 1,
                    Is_super: 0,
                    Asn_orig: 1
                }
            },
            {
                name: 'Sub-ownership Assertion',
                show_m: false,
                base_scores: 'H: 50 [30 if ref.] + 10 / (2 ^ ([32 or 128] – CIDR)), I: 15 / (2 ^ (scope))',
                subtype: 'Definite common owner',
                matrix: {
                    Base: 0.1,
                    Pc_type: 0,
                    Ed_diff: 1,
                    Pre_v_asn: 0.2,
                    Own_geo: 0,
                    Prop_orig: 0,
                    Low_adv: 1,
                    Prop_seen: 1,
                    As_in_path: 1,
                    Is_super: 0,
                    Asn_orig: 1
                }
            },
            {
                name: 'Sub-ownership Assertion',
                show_m: false,
                base_scores: 'H: 50 [30 if ref.] + 10 / (2 ^ ([32 or 128] – CIDR)), I: 15 / (2 ^ (scope))',
                subtype: 'Possible common owner / downstream',
                matrix: {
                    Base: 0.5,
                    Pc_type: 0,
                    Ed_diff: 1,
                    Pre_v_asn: 0.5,
                    Own_geo: 0.5,
                    Prop_orig: 0.5,
                    Low_adv: 1,
                    Prop_seen: 1,
                    As_in_path: 1,
                    Is_super: 0,
                    Asn_orig: 1
                }
            },
            {
                name: 'Sub-ownership Assertion',
                show_m: false,
                base_scores: 'H: 50 [30 if ref.] + 10 / (2 ^ ([32 or 128] – CIDR)), I: 15 / (2 ^ (scope))',
                subtype: 'No additional data',
                matrix: {
                    Base: 1,
                    Pc_type: 0,
                    Ed_diff: 1,
                    Pre_v_asn: 1,
                    Own_geo: 1,
                    Prop_orig: 1,
                    Low_adv: 1,
                    Prop_seen: 1,
                    As_in_path: 1,
                    Is_super: 0,
                    Asn_orig: 1
                }
            },
            {
                name: 'Ownership Assertion',
                show_m: false,
                base_scores: 'H: 40 [20 if ref.], I: 15',
                subtype: 'Direct data agrees',
                matrix: {
                    Base: 0.1,
                    Pc_type: 0,
                    Ed_diff: 1,
                    Pre_v_asn: 0,
                    Own_geo: 0,
                    Prop_orig: 0,
                    Low_adv: 1,
                    Prop_seen: 1,
                    As_in_path: 1,
                    Is_super: 0,
                    Asn_orig: 1
                }
            },
            {
                name: 'Ownership Assertion',
                show_m: false,
                base_scores: 'H: 40 [20 if ref.], I: 15',
                subtype: 'Definite common owner',
                matrix: {
                    Base: 0.1,
                    Pc_type: 0,
                    Ed_diff: 1,
                    Pre_v_asn: 0.2,
                    Own_geo: 0,
                    Prop_orig: 0,
                    Low_adv: 1,
                    Prop_seen: 1,
                    As_in_path: 1,
                    Is_super: 0,
                    Asn_orig: 1
                }
            },
            {
                name: 'Ownership Assertion',
                show_m: false,
                base_scores: 'H: 40 [20 if ref.], I: 15',
                subtype: 'Downstream',
                matrix: {
                    Base: 0.3,
                    Pc_type: 0,
                    Ed_diff: 1,
                    Pre_v_asn: 0.3,
                    Own_geo: 0.3,
                    Prop_orig: 0.3,
                    Low_adv: 1,
                    Prop_seen: 1,
                    As_in_path: 1,
                    Is_super: 0,
                    Asn_orig: 1
                }
            },
            {
                name: 'Ownership Assertion',
                show_m: false,
                base_scores: 'H: 40 [20 if ref.], I: 15',
                subtype: 'Potential common owner',
                matrix: {
                    Base: 0.5,
                    Pc_type: 0,
                    Ed_diff: 1,
                    Pre_v_asn: 0.5,
                    Own_geo: 0.5,
                    Prop_orig: 0.5,
                    Low_adv: 1,
                    Prop_seen: 1,
                    As_in_path: 1,
                    Is_super: 0,
                    Asn_orig: 1
                }
            },
            {
                name: 'Ownership Assertion',
                show_m: false,
                base_scores: 'H: 40 [20 if ref.], I: 15',
                subtype: 'No additional data',
                matrix: {
                    Base: 1,
                    Pc_type: 0,
                    Ed_diff: 1,
                    Pre_v_asn: 1,
                    Own_geo: 1,
                    Prop_orig: 1,
                    Low_adv: 1,
                    Prop_seen: 1,
                    As_in_path: 1,
                    Is_super: 0,
                    Asn_orig: 1
                }
            },
            {
                name: 'Path Change',
                show_m: false,
                base_scores: 'H: 0, I: 0',
                subtype: '<any> agrees / No ref. data',
                matrix: {
                    Base: 0,
                    Pc_type: 0.5,
                    Ed_diff: 0,
                    Pre_v_asn: 0,
                    Own_geo: 0,
                    Prop_orig: 0,
                    Low_adv: 0,
                    Prop_seen: 0,
                    As_in_path: 1,
                    Is_super: 0,
                    Asn_orig: 1
                }
            },
            {
                name: 'Path Change: Ongoing Ownership Assertion',
                show_m: false,
                base_scores: 'H: 0, I: 10',
                subtype: 'Parent data agrees / Definite common owner',
                matrix: {
                    Base: 1,
                    Pc_type: 0,
                    Ed_diff: 1,
                    Pre_v_asn: 0,
                    Own_geo: 0,
                    Prop_orig: 0,
                    Low_adv: 0,
                    Prop_seen: 1,
                    As_in_path: 1,
                    Is_super: 0,
                    Asn_orig: 1
                }
            },
            {
                name: 'Path Change: Ongoing Ownership Assertion',
                show_m: false,
                base_scores: 'H: 5, I: 10',
                subtype: 'Downstream / potential common owner',
                matrix: {
                    Base: 1,
                    Pc_type: 0.3,
                    Ed_diff: 1,
                    Pre_v_asn: 0.3,
                    Own_geo: 0.3,
                    Prop_orig: 0,
                    Low_adv: 0,
                    Prop_seen: 1,
                    As_in_path: 1,
                    Is_super: 0,
                    Asn_orig: 1
                }
            },
            {
                name: 'Path Change: Ongoing Ownership Assertion',
                show_m: false,
                base_scores: 'H: 10, I: 10',
                subtype: 'No additional data',
                matrix: {
                    Base: 1,
                    Pc_type: 0.5,
                    Ed_diff: 1,
                    Pre_v_asn: 0.5,
                    Own_geo: 0.5,
                    Prop_orig: 0,
                    Low_adv: 0,
                    Prop_seen: 1,
                    As_in_path: 1,
                    Is_super: 0,
                    Asn_orig: 1
                }
            },
            {
                name: 'Path Change: Ongoing Sub-ownership Assertion',
                show_m: false,
                base_scores: 'H: 0, I: 15 / (2 ^ scope)',
                subtype: 'Parent data agrees / Definite common owner',
                matrix: {
                    Base: 1,
                    Pc_type: 0,
                    Ed_diff: 1,
                    Pre_v_asn: 0,
                    Own_geo: 0,
                    Prop_orig: 0,
                    Low_adv: 0,
                    Prop_seen: 1,
                    As_in_path: 1,
                    Is_super: 0,
                    Asn_orig: 1
                }
            },
            {
                name: 'Path Change: Ongoing Sub-ownership Assertion',
                show_m: false,
                base_scores: 'H: 15, I: 15 / (2 ^ scope)',
                subtype: 'Downstream / potential common owner',
                matrix: {
                    Base: 1,
                    Pc_type: 0.3,
                    Ed_diff: 1,
                    Pre_v_asn: 0.3,
                    Own_geo: 0.3,
                    Prop_orig: 0,
                    Low_adv: 0,
                    Prop_seen: 1,
                    As_in_path: 1,
                    Is_super: 0,
                    Asn_orig: 1
                }
            },
            {
                name: 'Path Change: Ongoing Sub-ownership Assertion',
                show_m: false,
                base_scores: 'H: 25, I: 15 / (2 ^ scope)',
                subtype: 'No additional data',
                matrix: {
                    Base: 1,
                    Pc_type: 0.5,
                    Ed_diff: 1,
                    Pre_v_asn: 0.5,
                    Own_geo: 0.5,
                    Prop_orig: 0,
                    Low_adv: 0,
                    Prop_seen: 1,
                    As_in_path: 1,
                    Is_super: 0,
                    Asn_orig: 1
                }
            },
            {
                name: 'Path Change: Ongoing Super-ownership Assertion',
                show_m: false,
                base_scores: 'H: 0, I: 0',
                subtype: 'Any',
                matrix: {
                    Base: 0,
                    Pc_type: 0.3,
                    Ed_diff: 0,
                    Pre_v_asn: 0,
                    Own_geo: 0,
                    Prop_orig: 0,
                    Low_adv: 0,
                    Prop_seen: 1,
                    As_in_path: 1,
                    Is_super: 1,
                    Asn_orig: 1
                }
            },
        ],
        rowsPerPageItems: [],
        pagination: {
            rowsPerPage: 12,
        },
        rowsPerPageItems2: [],
        pagination2: {
            rowsPerPage: 22,
        },
        adj_buffer: true
    };
  },
  mounted () {
    
  },
  beforeMount() {
      this.changeCard(null);
      this.adjustMatrices();
  },
  methods: {
    changeCard: async function (type) {
        console.log('changing card colours: ' + type)
        
        this.selected = type
        if(this.$store.state.darkMode) {
            this.selected_colour = this.dark_select
            this.card_colours = {...this.dark_colours};
            if (this.selected) {
                this.card_colours[this.selected] = this.selected_colour
            }
        } else {
            this.selected_colour = this.light_select
            this.card_colours = {...this.light_colours};
            if (this.selected) {
                this.card_colours[this.selected] = this.selected_colour
            }
        }

    },
    adjTick: async function () {
        console.log("ADJTICK")
        this.adj_buffer = false
        this.$nextTick(() => {
            // Add the component back in
            this.adj_buffer = true;
        });
    },
    adjustMatrices: async function () {
        for (var i = 0 ; i < this.adj_scoring_matrix.length ; i++ ) {
            var matrix = this.adj_scoring_matrix[i].matrix
            var new_matrix = []
            var keys = Object.keys(matrix)
            for ( var j = 0 ; j < keys.length ; j++) {
                new_matrix.push({
                    'name': keys[j],
                    'value': matrix[keys[j]]
                })
            }
            this.adj_scoring_matrix[i].matrix = new_matrix
            
        }
    }
  },
  watch: {
      '$store.state.darkMode': function () {
          if(this.$store.state.darkMode) {
              this.selected_colour = this.dark_select
              this.card_colours = {...this.dark_colours};
              if (this.selected) {
                this.card_colours[this.selected] = this.selected_colour
              }
          } else {
              this.selected_colour = this.light_select
              this.card_colours = {...this.light_colours};
              if (this.selected) {
                this.card_colours[this.selected] = this.selected_colour
              }
          }
      },
      adj_scoring_matrix: {
          deep: true,
          handler () {
              this.adjTick()
          }
      }
  }
};
</script>


<style scoped>

    #parent {
        list-style: none;
        width: 100%;
        margin: 0;
        padding: 0;
        white-space: nowrap;
        overflow-x: auto;
        overflow-y: hidden;
    }


</style>